import { useCallback, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft, ShieldCheck } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, FetchParams } from '@/components/DataTable/types'
import { fetchGroupAdmins, type AdminMember } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A4.06 — Adminlar va huquqlar. GET /admin/groups/:id/members (owner+admin filtri,
 * yangi backend YO'Q). Granular huquqlar (permissions JSONB) = follow-up
 * (members endpointiga qo'shilgach ko'rsatiladi).
 */
export function GroupAdmins() {
  const t = useT()
  const { id = '' } = useParams()

  const columns = useMemo<AdminColumn<AdminMember>[]>(
    () => [
      {
        key: 'user',
        header: t.col.name,
        type: 'text',
        minWidth: 180,
        sortable: false,
        render: (r) => {
          const label =
            [r.first_name, r.last_name].filter(Boolean).join(' ') ||
            (r.username ? `@${r.username}` : `#${r.user_id}`)
          return (
            <Link to={`/users/${r.user_id}`} className="text-accent hover:underline">
              {label}
            </Link>
          )
        },
      },
      { key: 'username', header: t.col.username, type: 'text', width: 150 },
      {
        key: 'role',
        header: t.col.type,
        type: 'enum',
        width: 120,
        options: [
          { value: 'owner', label: t.groups.owner, status: 'banned' },
          { value: 'admin', label: t.groups.admin, status: 'review' },
        ],
      },
      { key: 'custom_title', header: t.groups.customTitle, type: 'text', minWidth: 150 },
      { key: 'joined_at', header: t.groups.joined, type: 'date', width: 150 },
    ],
    [t],
  )

  const fetcher = useCallback((params: FetchParams) => fetchGroupAdmins(Number(id), params), [id])

  return (
    <>
      <PageHeader
        title={t.groups.adminsTitle}
        screenId="A4.06"
        actions={
          <Link
            to={`/groups/${id}`}
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.groups.cardTitle}
          </Link>
        }
      />
      <DataTable<AdminMember>
        screenId="A4.06"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.user_id)}
        exportable
        emptyState={{ icon: <ShieldCheck size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
